import { useCallback, useEffect, useState } from 'react'
import { CalendarDays, Pencil, Plus, Trash2, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { dateBR } from '@/lib/format'
import { UserVacationsService, type UserVacation, type VacationSituation } from '@/services/modules/UserVacationsService'

const SITUATION: Record<VacationSituation, { label: string; variant: 'blue' | 'green' | 'gray' }> = {
  AGENDADAS: { label: 'Agendadas', variant: 'blue' },
  EM_ANDAMENTO: { label: 'Em férias', variant: 'green' },
  CONCLUIDAS: { label: 'Concluídas', variant: 'gray' },
}

/** Dias corridos entre duas datas ISO, contando as duas pontas. */
function daysBetween(start: string, end: string): number | null {
  if (!start || !end) return null
  const d = (Date.parse(`${end}T12:00:00`) - Date.parse(`${start}T12:00:00`)) / 86_400_000
  return d >= 0 ? Math.round(d) + 1 : null
}

type Draft = { id?: string; startDate: string; endDate: string; notes: string }

/**
 * Férias do usuário (aba RH). Cada período é salvo na hora — não depende do
 * botão "Salvar" do cadastro, como os documentos.
 */
export function UserVacations({ userId, canEdit }: { userId: string | null; canEdit: boolean }) {
  const [items, setItems] = useState<UserVacation[]>([])
  const [loading, setLoading] = useState(!!userId)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState<UserVacation | null>(null)

  const load = useCallback(() => {
    if (!userId) return Promise.resolve()
    return UserVacationsService.list(userId)
      .then(setItems)
      .catch(() => toast.error('Não foi possível carregar as férias.'))
      .finally(() => setLoading(false))
  }, [userId])
  useEffect(() => { void load() }, [load])

  if (!userId) {
    return <p className="text-xs text-gray-500">Salve o usuário primeiro para registrar férias.</p>
  }

  const current = items.find(v => v.situation === 'EM_ANDAMENTO')
  const next = [...items].filter(v => v.situation === 'AGENDADAS').sort((a, b) => a.startDate.localeCompare(b.startDate))[0]
  const draftDays = draft ? daysBetween(draft.startDate, draft.endDate) : null

  async function save() {
    if (!draft || !userId) return
    const e: Record<string, string> = {}
    if (!draft.startDate) e.startDate = 'Informe o início.'
    if (!draft.endDate) e.endDate = 'Informe o fim.'
    if (draft.startDate && draft.endDate && draft.endDate < draft.startDate) e.endDate = 'O fim não pode ser antes do início.'
    setErrors(e)
    if (Object.keys(e).length) return
    setSaving(true)
    try {
      const payload = { startDate: draft.startDate, endDate: draft.endDate, notes: draft.notes.trim() || null }
      if (draft.id) await UserVacationsService.update(userId, draft.id, payload)
      else await UserVacationsService.create(userId, payload)
      toast.success(draft.id ? 'Férias atualizadas.' : 'Férias registradas.')
      setDraft(null); setErrors({})
      await load()
    } catch (err) {
      const parsed = extractApiError(err)
      setErrors(Object.fromEntries(Object.entries(parsed.fields ?? {}).map(([k, v]) => [k.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase()), v])))
      toast.error({ title: 'Não foi possível salvar', description: parsed.message })
    } finally { setSaving(false) }
  }

  async function confirmRemove() {
    if (!removing || !userId) return
    try {
      await UserVacationsService.destroy(userId, removing.id)
      toast.success('Período removido.')
      setRemoving(null)
      await load()
    } catch (err) {
      toast.error({ title: 'Não foi possível remover', description: extractApiError(err).message })
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Férias</h3>
          <p className="text-xs text-gray-500">
            {current
              ? <>Em férias até <strong className="text-gray-700">{dateBR(current.endDate)}</strong>.</>
              : next ? <>Próximas férias em <strong className="text-gray-700">{dateBR(next.startDate)}</strong>.</>
                : 'Períodos de férias registrados para este usuário.'}
          </p>
        </div>
        {canEdit && !draft && (
          <Button size="sm" iconLeft={<Plus size={14} />} onClick={() => { setDraft({ startDate: '', endDate: '', notes: '' }); setErrors({}) }}>
            Registrar férias
          </Button>
        )}
      </div>

      {draft && (
        <div className="rounded-md bg-gray-50 p-4 ring-1 ring-gray-200">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[160px_160px_minmax(0,1fr)]">
            <Input label="Início" required type="date" value={draft.startDate} clearable={false} error={errors.startDate}
              onChange={e => { setDraft(d => d && { ...d, startDate: e.target.value }); setErrors(x => ({ ...x, startDate: '' })) }} />
            <Input label="Fim" required type="date" value={draft.endDate} clearable={false} error={errors.endDate}
              min={draft.startDate || undefined}
              onChange={e => { setDraft(d => d && { ...d, endDate: e.target.value }); setErrors(x => ({ ...x, endDate: '' })) }} />
            <Input label="Observação" value={draft.notes} maxLength={500} placeholder="Ex.: 1º período, abono de 10 dias"
              onChange={e => setDraft(d => d && { ...d, notes: e.target.value })} />
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-gray-600">
              {draftDays ? <><strong>{draftDays}</strong> dia{draftDays > 1 ? 's' : ''} corrido{draftDays > 1 ? 's' : ''}</> : 'Escolha o início e o fim.'}
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => { setDraft(null); setErrors({}) }} disabled={saving}>Cancelar</Button>
              <Button size="sm" onClick={save} loading={saving}>{draft.id ? 'Salvar alterações' : 'Salvar período'}</Button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p className="flex items-center gap-2 py-4 text-xs text-gray-500"><Loader2 size={14} className="animate-spin" /> Carregando</p>
      ) : items.length === 0 ? (
        !draft && (
          <div className="flex items-center gap-3 rounded-md border border-dashed border-gray-200 px-4 py-5 text-xs text-gray-500">
            <CalendarDays size={18} className="text-gray-400" /> Nenhum período de férias registrado.
          </div>
        )
      ) : (
        <div className="overflow-x-auto rounded-md ring-1 ring-gray-200">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 font-semibold">Período</th>
                <th className="px-3 py-2 text-center font-semibold">Dias</th>
                <th className="px-3 py-2 text-center font-semibold">Situação</th>
                <th className="px-3 py-2 font-semibold">Observação</th>
                {canEdit && <th className="w-20 px-3 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map(v => (
                <tr key={v.id}>
                  <td className="whitespace-nowrap px-3 py-2.5 text-gray-800">{dateBR(v.startDate)} a {dateBR(v.endDate)}</td>
                  <td className="px-3 py-2.5 text-center text-gray-700">{v.days}</td>
                  <td className="px-3 py-2.5 text-center"><Badge variant={SITUATION[v.situation].variant}>{SITUATION[v.situation].label}</Badge></td>
                  <td className="px-3 py-2.5 text-gray-600">
                    {v.notes || '—'}
                    {v.createdBy && <span className="block text-[11px] text-gray-400">Registrado por {v.createdBy}</span>}
                  </td>
                  {canEdit && (
                    <td className="px-3 py-2.5">
                      <div className="flex justify-end gap-1">
                        <button type="button" aria-label="Editar período" title="Editar"
                          onClick={() => { setDraft({ id: v.id, startDate: v.startDate, endDate: v.endDate, notes: v.notes ?? '' }); setErrors({}) }}
                          className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-brand-600">
                          <Pencil size={14} />
                        </button>
                        <button type="button" aria-label="Remover período" title="Remover" onClick={() => setRemoving(v)}
                          className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-rose-50 hover:text-rose-600">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={confirmRemove}
        title="Remover período de férias?"
        description={removing ? `Remover as férias de ${dateBR(removing.startDate)} a ${dateBR(removing.endDate)}?` : ''}
        confirmLabel="Remover"
        variant="danger"
      />
    </div>
  )
}
